/**
 * Secure logging utility with error sanitization
 * 
 * This utility provides a safer alternative to direct console logging
 * that can be controlled based on environment and log level.
 * 
 * In production, sensitive logs are suppressed to prevent leaking
 * sensitive information. Includes advanced error sanitization to
 * prevent raw hex data exposure in error messages.
 */

// Log levels
export enum LogLevel {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3,
    NONE = 4
}

// Error sanitization interfaces
interface SanitizedError {
    message: string;
    code: string;
    reason: string;
    context: string;
    stack: string;
    originalErrorId?: string; // For server-side correlation
}

interface ErrorSanitizationOptions {
    preserveAddresses?: boolean;
    preserveErrorCodes?: boolean;
    logFullErrorServerSide?: boolean;
    context?: string;
}

// Environment-based logging configuration
const getEnvironmentLogConfig = () => {
    const environment = import.meta.env.VITE_ENVIRONMENT || 'development';
    const customLogLevel = import.meta.env.VITE_LOG_LEVEL;
    const enableDebugLogs = import.meta.env.VITE_ENABLE_DEBUG_LOGS !== 'false';

    // If custom log level is specified, use it
    if (customLogLevel) {
        const levelMap: Record<string, LogLevel> = {
            'DEBUG': LogLevel.DEBUG,
            'INFO': LogLevel.INFO,
            'WARN': LogLevel.WARN,
            'ERROR': LogLevel.ERROR,
            'NONE': LogLevel.NONE
        };
        const level = levelMap[customLogLevel.toUpperCase()] ?? LogLevel.INFO;
        return {
            minLevel: level,
            enabled: level !== LogLevel.NONE,
            enabledInProduction: true
        };
    }

    // Environment-based defaults
    switch (environment) {
        case 'prod':
        case 'production':
            return {
                minLevel: LogLevel.ERROR,
                enabled: true,
                enabledInProduction: true
            };
        case 'dev':
        case 'development':
            return {
                minLevel: LogLevel.INFO,
                enabled: enableDebugLogs,
                enabledInProduction: true
            };
        case 'local':
        case 'localhost':
        default:
            return {
                minLevel: LogLevel.DEBUG,
                enabled: enableDebugLogs,
                enabledInProduction: true
            };
    }
};

// Default configuration based on environment
const config = getEnvironmentLogConfig();

/**
 * Configure the logger
 * @param options - Configuration options
 */
export const configureLogger = (options: {
    minLevel?: LogLevel;
    enabled?: boolean;
    enabledInProduction?: boolean;
}) => {
    if (options.minLevel !== undefined) {
        config.minLevel = options.minLevel;
    }
    if (options.enabled !== undefined) {
        config.enabled = options.enabled;
    }
    if (options.enabledInProduction !== undefined) {
        config.enabledInProduction = options.enabledInProduction;
    }
};

/**
 * Check if logging is enabled for the given level
 * @param level - The log level to check
 * @returns boolean indicating if logging is enabled
 */
const isLoggingEnabled = (level: LogLevel): boolean => {
    // In production, respect the enabledInProduction flag
    if (process.env.NODE_ENV === 'production' && !config.enabledInProduction) {
        return false;
    }

    // Otherwise, check if logging is enabled and the level is high enough
    return config.enabled && level >= config.minLevel;
};

/**
 * Truncate hex data for secure logging
 * @param hex - Hex string to truncate
 * @returns Truncated hex string
 */
const truncateHexData = (hex: string): string => {
    if (hex.length <= 20) return hex;
    return `${hex.substring(0, 10)}...${hex.substring(hex.length - 4)}`;
};

/**
 * Redact sensitive information from log messages
 * @param args - The arguments to redact
 * @returns Redacted arguments
 */
const redactSensitiveInfo = (args: any[]): any[] => {
    return args.map(arg => {
        if (typeof arg === 'string') {
            // Redact private keys, mnemonic phrases, and other sensitive data
            return arg
                // Redact private keys (0x followed by 64 hex chars)
                .replace(/0x[0-9a-f]{64}/gi, '[REDACTED_PRIVATE_KEY]')
                // Truncate long hex data (function data, encrypted data, etc.)
                .replace(/0x[0-9a-f]{20,}/gi, (match) => truncateHexData(match))
                // Truncate long encrypted strings (without 0x prefix)
                .replace(/[0-9a-f]{40,}/gi, (match) => {
                    // Only truncate if it looks like encrypted data (long hex string)
                    if (match.length > 40) {
                        return `${match.substring(0, 10)}...${match.substring(match.length - 4)}`;
                    }
                    return match;
                })
                // Redact potential mnemonic phrases (sequence of 12-24 words)
                .replace(/\b(\w+\s+){11,23}\w+\b/g, '[REDACTED_MNEMONIC]')
                // Redact potential passwords
                .replace(/password[=:]\s*['"]?[^'")\s]+['"]?/gi, 'password=[REDACTED]')
                // Redact potential API keys
                .replace(/api[_-]?key[=:]\s*['"]?[^'")\s]+['"]?/gi, 'apiKey=[REDACTED]');
        } else if (typeof arg === 'object' && arg !== null) {
            // For objects, create a deep copy and redact sensitive fields
            try {
                const copy = JSON.parse(JSON.stringify(arg));

                // List of sensitive field names to redact
                const sensitiveFields = [
                    'privateKey', 'private_key', 'mnemonic', 'seed', 'password',
                    'secret', 'apiKey', 'api_key', 'token', 'accessToken', 'access_token'
                ];

                // Recursive function to redact fields in objects
                const redactObject = (obj: any) => {
                    if (typeof obj !== 'object' || obj === null) return;

                    Object.keys(obj).forEach(key => {
                        // Check if this is a sensitive field
                        if (sensitiveFields.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
                            obj[key] = '[REDACTED]';
                        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                            // Recursively process nested objects
                            redactObject(obj[key]);
                        }
                    });
                };

                redactObject(copy);
                return copy;
            } catch (e) {
                // If we can't process the object, return it as is
                return arg;
            }
        }
        return arg;
    });
};

// Error counter for unique error IDs
let errorCounter = 0;

// Error sanitization patterns
const HEX_PATTERN = /0x[a-fA-F0-9]{8,}/g;
const LONG_HEX_PATTERN = /0x[a-fA-F0-9]{40,}/g;
const TRANSACTION_DATA_PATTERN = /"data":"0x[a-fA-F0-9]+"/g;
const SIGNATURE_PATTERN = /0x[a-fA-F0-9]{130}/g;

/**
 * Sanitize error for public display and logging
 */
const sanitizeErrorInternal = (
    error: any,
    options: ErrorSanitizationOptions = {}
): SanitizedError => {
    const {
        preserveAddresses = true,
        preserveErrorCodes = true,
        logFullErrorServerSide = true,
        context = 'Unknown'
    } = options;

    // Generate unique error ID for correlation
    const errorId = `ERR_${Date.now()}_${++errorCounter}`;

    // Log full error server-side for debugging (if enabled)
    if (logFullErrorServerSide && process.env.NODE_ENV === 'development') {
        console.group(`🔒 SECURE ERROR LOG [${errorId}] - ${context}`);
        console.warn('⚠️ This contains sensitive data - not shown in production logs');
        console.error('Full error details:', error);
        console.groupEnd();
    }

    // Extract basic error information
    let message = extractErrorMessage(error);
    let code = extractErrorCode(error);
    let reason = extractErrorReason(error);
    let stack = extractErrorStack(error);

    // Sanitize each component
    message = sanitizeMessage(message, { preserveAddresses, preserveErrorCodes });
    reason = sanitizeMessage(reason, { preserveAddresses, preserveErrorCodes });
    stack = sanitizeMessage(stack, { preserveAddresses, preserveErrorCodes });

    return {
        message,
        code,
        reason,
        context,
        stack,
        originalErrorId: errorId
    };
};

/**
 * Extract error message from various error formats
 */
const extractErrorMessage = (error: any): string => {
    if (typeof error === 'string') return error;
    if (error?.message) return String(error.message);
    if (error?.reason) return String(error.reason);
    if (error?.data?.message) return String(error.data.message);
    return 'Unknown error occurred';
};

/**
 * Extract error code from various error formats
 */
const extractErrorCode = (error: any): string => {
    if (error?.code) return String(error.code);
    if (error?.data?.code) return String(error.data.code);
    if (error?.error?.code) return String(error.error.code);
    return '';
};

/**
 * Extract error reason from various error formats
 */
const extractErrorReason = (error: any): string => {
    if (error?.reason) return String(error.reason);
    if (error?.data?.message) return String(error.data.message);
    return '';
};

/**
 * Extract stack trace (sanitized)
 */
const extractErrorStack = (error: any): string => {
    if (error?.stack) {
        // Only return the first few lines of stack trace, sanitized
        const stackLines = String(error.stack).split('\n').slice(0, 3);
        return stackLines.join('\n');
    }
    return '';
};

/**
 * Sanitize message by removing sensitive data
 */
const sanitizeMessage = (
    message: string,
    options: { preserveAddresses?: boolean; preserveErrorCodes?: boolean }
): string => {
    if (!message) return '';

    let sanitized = message;

    // Remove transaction data (most sensitive)
    sanitized = sanitized.replace(TRANSACTION_DATA_PATTERN, '"data":"[REDACTED_TX_DATA]"');

    // Remove long hex strings (signatures, encoded data)
    sanitized = sanitized.replace(LONG_HEX_PATTERN, (match) => {
        if (options.preserveAddresses && match.length === 42) {
            // Preserve addresses (40 chars + 0x)
            return match;
        }
        if (options.preserveErrorCodes && match.length <= 10) {
            // Preserve short error codes
            return match;
        }
        // Redact long hex data
        return `0x[REDACTED_${match.length - 2}_CHARS]`;
    });

    // Remove signatures specifically
    sanitized = sanitized.replace(SIGNATURE_PATTERN, '0x[REDACTED_SIGNATURE]');

    // Remove any remaining long hex strings
    sanitized = sanitized.replace(/0x[a-fA-F0-9]{20,}/g, (match) => {
        if (options.preserveAddresses && match.length === 42) {
            return match;
        }
        return `0x[REDACTED_${match.length - 2}_CHARS]`;
    });

    // Clean up JSON formatting artifacts
    sanitized = sanitized.replace(/,\s*"data":"[^"]*"/g, ',"data":"[REDACTED]"');
    sanitized = sanitized.replace(/"transaction":\{[^}]*\}/g, '"transaction":{"[REDACTED]":"[REDACTED]"}');

    return sanitized;
};

/**
 * Create user-friendly error message for UI display
 */
const createUserFriendlyMessage = (sanitizedError: SanitizedError): string => {
    const { message, code, context } = sanitizedError;

    // Smart contract specific errors
    if (message.includes('execution reverted')) {
        if (message.includes('custom error')) {
            return `❌ Smart contract rejected the transaction. Please verify your parameters and permissions.`;
        }
        return `❌ Transaction was rejected by the smart contract. Check your inputs and try again.`;
    }

    // Gas estimation errors
    if (code === 'UNPREDICTABLE_GAS_LIMIT' || message.includes('gas')) {
        return `❌ Unable to estimate gas for this transaction. The transaction may fail or require manual gas settings.`;
    }

    // Network errors
    if (message.includes('network') || message.includes('connection')) {
        return `❌ Network connection error. Please check your connection and try again.`;
    }

    // Signature errors
    if (message.includes('signature') || context.includes('Signature')) {
        return `❌ Signature collection failed. Please ensure all required signatures are valid.`;
    }

    // Generic fallback
    return `❌ Transaction failed. Please check your parameters and try again.`;
};

/**
 * Get debugging suggestions based on error type
 */
const getDebuggingSuggestions = (sanitizedError: SanitizedError): string[] => {
    const { message, code, context } = sanitizedError;
    const suggestions: string[] = [];

    if (message.includes('execution reverted')) {
        suggestions.push('The smart contract rejected the transaction');
        suggestions.push('Verify all function parameters are correct');
        suggestions.push('Check if you have the required permissions');
    }

    if (code === 'UNPREDICTABLE_GAS_LIMIT') {
        suggestions.push('The transaction may fail - check parameters');
        suggestions.push('Try increasing the gas limit manually');
        suggestions.push('Verify the contract function exists and is callable');
    }

    if (context.includes('MultiSig') || message.includes('multisig')) {
        suggestions.push('Ensure you have collected enough valid signatures');
        suggestions.push('Verify all signers are authorized MultiSig owners');
        suggestions.push('Check that signatures are in the correct format');
    }

    if (context.includes('Signature')) {
        suggestions.push('Verify wallet is connected and unlocked');
        suggestions.push('Check that you are signing with the correct account');
        suggestions.push('Ensure the signature data is properly formatted');
    }

    // Default suggestions if none match
    if (suggestions.length === 0) {
        suggestions.push('Check your wallet connection');
        suggestions.push('Verify contract addresses are correct');
        suggestions.push('Ensure you have sufficient gas and funds');
    }

    return suggestions;
};

/**
 * Secure logger object
 */
export const logger = {
    /**
     * Log a debug message
     * @param args - Arguments to log
     */
    debug: (...args: any[]): void => {
        if (isLoggingEnabled(LogLevel.DEBUG)) {
            console.debug(...redactSensitiveInfo(args));
        }
    },

    /**
     * Log an info message
     * @param args - Arguments to log
     */
    info: (...args: any[]): void => {
        if (isLoggingEnabled(LogLevel.INFO)) {
            console.info(...redactSensitiveInfo(args));
        }
    },

    /**
     * Log a warning message
     * @param args - Arguments to log
     */
    warn: (...args: any[]): void => {
        if (isLoggingEnabled(LogLevel.WARN)) {
            console.warn(...redactSensitiveInfo(args));
        }
    },

    /**
     * Log an error message
     * @param args - Arguments to log
     */
    error: (...args: any[]): void => {
        if (isLoggingEnabled(LogLevel.ERROR)) {
            console.error(...redactSensitiveInfo(args));
        }
    },

    /**
     * Log a message (alias for info)
     * @param args - Arguments to log
     */
    log: (...args: any[]): void => {
        if (isLoggingEnabled(LogLevel.INFO)) {
            console.log(...redactSensitiveInfo(args));
        }
    },

    /**
     * Disable all logging
     */
    disable: (): void => {
        config.enabled = false;
    },

    /**
     * Enable logging
     */
    enable: (): void => {
        config.enabled = true;
    },

    /**
     * Get the current logger configuration
     * @returns The current configuration
     */
    getConfig: () => ({ ...config }),

    /**
     * Sanitize error for secure logging and UI display
     * @param error - The error to sanitize
     * @param options - Sanitization options
     * @returns Sanitized error object
     */
    sanitizeError: (error: any, options?: ErrorSanitizationOptions): SanitizedError => {
        return sanitizeErrorInternal(error, options);
    },

    /**
     * Create user-friendly error message
     * @param error - The error to process
     * @param context - Error context
     * @returns User-friendly error message
     */
    createUserFriendlyErrorMessage: (error: any, context?: string): string => {
        const sanitized = sanitizeErrorInternal(error, { context });
        return createUserFriendlyMessage(sanitized);
    },

    /**
     * Get debugging suggestions for an error
     * @param error - The error to analyze
     * @param context - Error context
     * @returns Array of debugging suggestions
     */
    getErrorSuggestions: (error: any, context?: string): string[] => {
        const sanitized = sanitizeErrorInternal(error, { context });
        return getDebuggingSuggestions(sanitized);
    },

    /**
     * Log a sanitized error (secure version of error logging)
     * @param error - The error to log
     * @param context - Error context
     * @param options - Sanitization options
     */
    secureError: (error: any, context?: string, options?: ErrorSanitizationOptions): void => {
        if (isLoggingEnabled(LogLevel.ERROR)) {
            const sanitized = sanitizeErrorInternal(error, { ...options, context });
            console.error(`🔒 [${sanitized.originalErrorId}] ${sanitized.context}:`, sanitized.message);

            if (sanitized.code) {
                console.error(`   Code: ${sanitized.code}`);
            }
            if (sanitized.reason) {
                console.error(`   Reason: ${sanitized.reason}`);
            }

            // Show suggestions in development
            if (process.env.NODE_ENV === 'development') {
                const suggestions = getDebuggingSuggestions(sanitized);
                if (suggestions.length > 0) {
                    console.error('   Suggestions:', suggestions);
                }
            }
        }
    }
};

// Export helper functions for backward compatibility and convenience
export const sanitizeError = (error: any, options?: ErrorSanitizationOptions): SanitizedError => {
    return logger.sanitizeError(error, options);
};

export const createUserFriendlyErrorMessage = (error: any, context?: string): string => {
    return logger.createUserFriendlyErrorMessage(error, context);
};

export const getErrorSuggestions = (error: any, context?: string): string[] => {
    return logger.getErrorSuggestions(error, context);
};

// Export types for external use
export type { SanitizedError, ErrorSanitizationOptions };

// Export a default instance
export default logger;
